import { getFileSizeString } from "cpl-react-common/Helpers/FileHelpers";
import React, { useState } from "react";
import { ChatDocument } from "app/modules/aiAssistant/AIAssistantSlice";

type UseFilePickerOptions = {
  files: ChatDocument[];
  disabled?: boolean;
  filesLeftToAttach: number;
  acceptedFileTypes?: string[];
  maximumFileSizeBytes?: number;
  displayError?: (error: string) => void;
  onFilesAdded?: (files: File[]) => void;
};

// Checks and picks up new files; the caller owns the list of files already added
export default function useFilePicker({
  files,
  disabled,
  filesLeftToAttach,
  acceptedFileTypes = ["*"],
  maximumFileSizeBytes,
  displayError,
  onFilesAdded,
}: UseFilePickerOptions) {
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [isDragActive, setDragActive] = useState<boolean>(false);

  const openFileDialog = React.useCallback(() => {
    if (disabled) return;
    fileInputRef.current?.click();
  }, [disabled]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (!e.target.files || e.target.files.length === 0) return;
    addNewFiles(e.target.files);
    e.target.value = "";
  };

  const handleDragEnter = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setDragActive(true);
  };

  const handleDragOver = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = disabled ? "none" : "copy";
  };

  const handleDragLeave = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Moving onto a child element also fires dragleave
    if (disabled || e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;

    setDragActive(false);
    const droppedFiles = e.dataTransfer.files;
    if (!droppedFiles || droppedFiles.length === 0) return;
    addNewFiles(droppedFiles);
  };

  const addNewFiles = (newFiles: FileList) => {
    const errors: string[] = [];
    let filesToAdd: File[] = [];

    if (newFiles.length > filesLeftToAttach) {
      errors.push(`Cannot add ${newFiles.length} files. Only ${filesLeftToAttach} more can be attached`);
    } else {
      filesToAdd = Array.from(newFiles).filter((file) => {
        const fileExtension = file.name.split(".").at(-1)?.toLowerCase();
        if (files.some((existing) => existing.name === file.name)) {
          errors.push(`Cannot add file ${file.name} as it has already been added`);
          return false;
        } else if (
          acceptedFileTypes.every((value) => value !== "*" && value !== fileExtension && value !== file.type)
        ) {
          errors.push(`Cannot add file ${file.name} as it is not a valid file type`);
          return false;
        } else if (maximumFileSizeBytes !== undefined && file.size > maximumFileSizeBytes) {
          errors.push(
            `Cannot add file ${file.name} as it exceeds the maximum file size of ${getFileSizeString(
              maximumFileSizeBytes
            )}`
          );
          return false;
        }

        return true;
      });
    }

    if (filesToAdd.length > 0) onFilesAdded?.(filesToAdd);
    errors.forEach((error) => displayError?.(error));
  };

  return {
    fileInputRef,
    isDragActive,
    openFileDialog,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleChange,
  };
}
